import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Brain, Sparkles, GraduationCap, Workflow, Route, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { TranslatedText } from '@/components/TranslatedText';
import AIToolsMarquee from '@/components/sales/AIToolsMarquee';

const pillars = [
  {
    icon: GraduationCap,
    title: "5 Complete Academies",
    desc: "500+ video lessons across Content, Traffic, E-Commerce, Newsletters & Automation — from zero to monetization.",
  },
  {
    icon: Brain,
    title: "Aura AI Personal Teacher",
    desc: "Your own AI professor available 24/7. Ask anything, get instant guidance tailored to your exact module and progress.",
  },
  {
    icon: Sparkles,
    title: "Built-In AI Productivity Tools",
    desc: "Image creator, video scripts, captions, niche analysis & content planning — no ChatGPT or Canva subscriptions needed.",
  },
];

const extras = [
  { icon: Workflow, text: "Step-by-step workflow structure" },
  { icon: Route, text: "Clear monetization paths" },
  { icon: RefreshCw, text: "Continuous updates & new tools" },
];

const isFor = [
  "People who want online income without becoming influencers",
  "Beginners who prefer structure over hype",
  "Builders who want systems, not motivation",
];

const isNotFor = [
  "Overnight success seekers",
  "People looking for done-for-you businesses",
  "Anyone unwilling to follow step-by-step processes",
];

export const LightProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} id="problem" className="py-12 sm:py-20 px-4 bg-background">
      <div className="max-w-5xl mx-auto">
        {/* Why Us */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-sm font-medium text-muted-foreground tracking-widest uppercase mb-4 block">
            <TranslatedText>Why us?</TranslatedText>
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 px-2">
            <TranslatedText>Everything You Need. One Platform.</TranslatedText>
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto mb-10">
            <TranslatedText>Academy + AI Teacher + AI Tools — built together so you never need another subscription.</TranslatedText>
          </p>

          {/* Three Pillars */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {pillars.map((pillar, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                className="p-5 rounded-2xl bg-card border border-border text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                  <pillar.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-sm font-bold text-foreground mb-1.5">
                  <TranslatedText>{pillar.title}</TranslatedText>
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <TranslatedText>{pillar.desc}</TranslatedText>
                </p>
              </motion.div>
            ))}
          </div>

          {/* Extra points */}
          <div className="flex flex-wrap justify-center gap-3 max-w-xl mx-auto">
            {extras.map((point, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.35 + i * 0.05 }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card border border-border"
              >
                <point.icon className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-foreground">
                  <TranslatedText>{point.text}</TranslatedText>
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* AI Tools Logo Slider */}
        <AIToolsMarquee />

        {/* Who This Is For / Not For */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid sm:grid-cols-2 gap-4 sm:gap-6 mt-12"
        >
          {/* Is For */}
          <div className="p-5 sm:p-6 rounded-2xl bg-card border border-border">
            <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <TranslatedText>This is for:</TranslatedText>
            </h3>
            <ul className="space-y-3">
              {isFor.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    <TranslatedText>{item}</TranslatedText>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Not For */}
          <div className="p-5 sm:p-6 rounded-2xl bg-card border border-border">
            <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-destructive" />
              <TranslatedText>This is NOT for:</TranslatedText>
            </h3>
            <ul className="space-y-3">
              {isNotFor.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    <TranslatedText>{item}</TranslatedText>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LightProblemSection;
